import { log, PushProcessingNotification } from "@repo/helpers";
import {
	BasicDialog,
	Button,
	ButtonGroup,
	ButtonGroupOption,
	ButtonSize,
	ButtonTypes,
	Col,
	Loading,
	Row,
	Tabs,
	TabsList,
	TabsTrigger,
} from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import * as Queries from "../../api/queries";
import { useRepApi, useRepQueries } from "../../api/repApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import { usePermissionsCheck } from "../../helpers/permissions";
import { useUpdateRoute } from "../../helpers/urlHooks";
import RepStatusButton, { StatusButton } from "../../models/repStatusButton";
import { Deactivation } from "./deactivation";
import { OnboardingTabs } from "./onboardingTabs";
import style from "./style.module.scss";

export function MultiStatusUpdate() {
	const { repId } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data, loaded } = useRepSegmentsQuery();
	const { segmentId } = useSegmentContext();

	const segments = useMemo(() => {
		if (!data?.length) return data;
		if (!segmentId) return data;
		return data.filter((d) => d.id == segmentId);
	}, [data, segmentId]);

	return (
		<div>
			<div className={style.statusButtonContainer}>
				{renderAllStateStatusUpdate()}
				<OnboardingTabs
					renderSegment={(segment) => <SegmentUpdate key={segment.id} repSegmentId={segment.id} />}
				/>
			</div>
			<hr className={style.horizontalDivider} />
		</div>
	);

	function renderAllStateStatusUpdate() {
		if (!loaded || segments.length <= 1) return <StatusUpdate repId={repId} />;
		//If multiple states/segments, display with the same formatting as segments
		return (
			<Tabs value="1">
				<TabsList>
					<TabsTrigger key={"1"} value={"1"} className={style.flexWithGap}>
						All States
					</TabsTrigger>
				</TabsList>
				<StatusUpdate repId={repId} />
			</Tabs>
		);
	}
}

interface Props {
	repId?: number;
	repSegmentId?: number;
}

export function StatusUpdate(props: Props) {
	const { repId } = props;
	const { useStatusButtonsQuery } = useRepQueries();
	const {
		data: statusButtons,
		loaded: statusButtonsLoaded,
		clearQuery: clearStatusButtons,
	} = useStatusButtonsQuery(repId);

	if (!repId) return null;

	return (
		<StatusButtons
			buttons={statusButtons}
			loaded={statusButtonsLoaded}
			clear={clearStatusButtons}
			repId={repId}
			repSegmentId={undefined}
		/>
	);
}

export function SegmentUpdate(props: Props) {
	const { repSegmentId } = props;
	const { useSegmentStatusButtonsQuery } = useRepQueries();
	const {
		data: statusButtons,
		loaded: statusButtonsLoaded,
		clearQuery: clearStatusButtons,
	} = useSegmentStatusButtonsQuery(repSegmentId);

	if (!repSegmentId) return null;

	return (
		<StatusButtons
			buttons={statusButtons}
			loaded={statusButtonsLoaded}
			clear={clearStatusButtons}
			repId={undefined}
			repSegmentId={repSegmentId}
		/>
	);
}

interface ButtonProps {
	repId: number | undefined;
	repSegmentId: number | undefined;
	buttons: RepStatusButton[];
	clear: () => Promise<void>;
	loaded: boolean;
}

export function StatusButtons(props: ButtonProps) {
	const { repId, repSegmentId, buttons, clear, loaded } = props;
	const { rep } = useRepContext();
	const { updateStatus } = useRepApi();
	const { useRepInfoQuery } = useRepQueries();
	const updateRoute = useUpdateRoute();

	const { clearQuery: clearRepInfoQuery } = useRepInfoQuery();
	const queryClient = useQueryClient();

	const [modalAction, setModalAction] = useState<string | undefined>(undefined);

	const { fullAPReadOnly } = usePermissionsCheck();

	const { groupedButtons, individualButtons } = useMemo(() => {
		if (!buttons) return { groupedButtons: [], individualButtons: [] };

		const discipline: RepStatusButton[] = [];
		const individual: RepStatusButton[] = [];

		buttons.forEach((btn) => {
			switch (btn.Type) {
				case StatusButton.DeactivateAll:
				case StatusButton.Blocklist:
				case StatusButton.StartSuspension:
				case StatusButton.Terminate:
					discipline.push(btn);
					break;
				default:
					individual.push(btn);
					break;
			}
		});

		return { groupedButtons: [discipline], individualButtons: individual };
	}, [buttons]);

	const updateRepStatus = useCallback(
		async (btn: RepStatusButton) => {
			const notification = PushProcessingNotification("Updating Rep Status...");
			try {
				if (repId) await updateStatus(btn.Action, repId);
				else await updateStatus(btn.Action, undefined, repSegmentId);
				notification.success("Rep Status Updated!");
			} catch (err) {
				notification.error("Failed to update rep status", err);
			} finally {
				await clear();
				await clearRepInfoQuery();

				await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.REP] });
			}
		},
		[clear, clearRepInfoQuery, queryClient, repId, repSegmentId, updateStatus],
	);

	const showDeactivationFormModal = useCallback(function (action: string) {
		setModalAction(action);
	}, []);

	const onClickStatusButton = useCallback(
		async (btn: RepStatusButton) => {
			switch (btn.Type) {
				case StatusButton.StartSuspension: {
					updateRoute(-1, "Discipline", { action: "suspend" });
					break;
				}
				case StatusButton.Blocklist: {
					updateRoute(-1, "Discipline", { action: "blocklist" });
					break;
				}
				case StatusButton.Deactivate: {
					showDeactivationFormModal(btn.Action);
					break;
				}
				case StatusButton.DeactivateAll: {
					showDeactivationFormModal(btn.Action);
					break;
				}
				default: {
					await updateRepStatus(btn);
					break;
				}
			}
		},
		[updateRoute, updateRepStatus, showDeactivationFormModal],
	);

	return (
		<>
			<Row tag="section" className={style.statusButtons}>
				<Col md="12">
					<Row>
						<Loading loading={!loaded}>
							{groupedButtons.map((grouped) => (
								<ButtonGroupComponent
									buttons={grouped}
									fullAPReadOnly={fullAPReadOnly}
									onClickStatusButton={onClickStatusButton}
								/>
							))}
							{individualButtons?.map((btn) => (
								<StatusButtonComponent
									key={btn.Name}
									button={btn}
									fullAPReadOnly={fullAPReadOnly}
									onClickStatusButton={onClickStatusButton}
								/>
							))}
						</Loading>
					</Row>
				</Col>
			</Row>
			<BasicDialog
				title="Deactivate Agent"
				open={!!modalAction}
				onOpenChange={(open) => !open && setModalAction(undefined)}
				hideTrigger
				fullWidth={false}
				allowOverflow
			>
				{modalAction && (
					<div className={style.statusUpdateContent}>
						<Deactivation
							rep={rep}
							repSegmentId={repSegmentId}
							action={modalAction}
							close={() => setModalAction(undefined)}
						/>
					</div>
				)}
			</BasicDialog>
		</>
	);
}

interface ButtonGroupProps {
	buttons: RepStatusButton[];
	fullAPReadOnly: boolean;
	onClickStatusButton: (btn: RepStatusButton) => Promise<void>;
}

function ButtonGroupComponent({ buttons, fullAPReadOnly, onClickStatusButton }: ButtonGroupProps) {
	if (!buttons.length) return null;

	const availableButtons = buttons.filter((btn) => btn.CanDo);
	if (!availableButtons.length) return null;

	const sortedButtons = sortGroupedButtons(availableButtons);

	const options = sortedButtons.map((btn) => ({
		id: btn.Name,
		label: btn.Name,
		disabled: !btn.CanDo || fullAPReadOnly,
	}));

	const defaultButton = sortedButtons.find((btn) => btn.CanDo && !fullAPReadOnly) ?? sortedButtons[0];

	async function handleButtonClick(selectedOption: ButtonGroupOption) {
		const button = sortedButtons.find((btn) => btn.Name === selectedOption.id);
		if (button) {
			await onClickStatusButton(button);
		} else {
			log("could not find button", "error");
		}
	}

	return (
		<Col md="2" key="button-group" fit>
			<ButtonGroup
				options={options}
				selectedId={defaultButton?.Name}
				onButtonClick={handleButtonClick}
				types={getButtonTypes(defaultButton.Type)}
				size={ButtonSize.large}
				disabled={fullAPReadOnly}
			/>
		</Col>
	);
}

interface StatusButtonProps {
	button: RepStatusButton;
	fullAPReadOnly: boolean;
	onClickStatusButton: (btn: RepStatusButton) => Promise<void>;
}

function StatusButtonComponent({ button, fullAPReadOnly, onClickStatusButton }: StatusButtonProps) {
	return (
		<Col md="2" title={button.CantDoReason} fit>
			<Button
				types={getButtonTypes(button.Type)}
				size={ButtonSize.large}
				disabled={!button.CanDo || fullAPReadOnly}
				onClick={() => onClickStatusButton(button)}
			>
				{button.Name}
			</Button>
		</Col>
	);
}

function sortGroupedButtons(buttons: RepStatusButton[]): RepStatusButton[] {
	return buttons.sort((a, b) => {
		if (a.Type === StatusButton.StartSuspension && b.Type !== StatusButton.StartSuspension) {
			return -1;
		}
		if (b.Type === StatusButton.StartSuspension && a.Type !== StatusButton.StartSuspension) {
			return 1;
		}
		return 0;
	});
}

function getButtonTypes(type: StatusButton): ButtonTypes[] {
	switch (type) {
		case StatusButton.Activate:
		case StatusButton.CompleteOnboarding:
		case StatusButton.EndSuspension:
		case StatusButton.ContingentlyActivate:
		case StatusButton.SetOffHold:
		case StatusButton.ForceActivate:
			return [ButtonTypes.Green];
		case StatusButton.StartOnboarding:
		case StatusButton.ExtendContingentStart:
		case StatusButton.SetOnHold:
		case StatusButton.VersionCopy:
			return [ButtonTypes.Primary];
		case StatusButton.Deactivate:
		case StatusButton.StartSuspension:
		case StatusButton.Terminate:
		case StatusButton.Blocklist:
			return [ButtonTypes.Danger];
		default:
			return [ButtonTypes.Primary];
	}
}
